<template>
  <v-card border>
    <v-card-text>
      <b-section-loader :loading="$loading.isLoading('init')">
        <div class="d-flex align-center justify-space-between ga-3 mb-3">
          <div class="d-flex align-center ga-3">
            <div class="icon--blue">
              <v-icon icon="mdi-file-document-outline" size="23"></v-icon>
            </div>
            <div>
              <div class="font-weight-bold font-size-18">
                {{ documentSource.definition?.name }}
                <span v-if="documentSource.registrationNumber">
                  №{{ documentSource.registrationNumber }}
                </span>
              </div>
              <div class="d-flex ga-2 mb-1">
                <div class="text-disabled">
                  {{ new CustomDate(documentSource.created_at).toString() }}
                </div>
              </div>
              <div class="d-flex ga-3">
                <v-chip
                  :color="getStatusColor(documentSource.status)"
                  rounded="pill"
                  density="compact">
                  <b>{{
                    keyToLabel(documentSource.status, 'documentStatus')
                  }}</b>
                </v-chip>
              </div>
            </div>
          </div>

          <div class="d-flex align-center ga-3">
            <template
              v-if="delegationData.canApprove && delegationPendingParticipant">
              <v-menu>
                <template #activator="{ props }">
                  <v-btn v-bind="props">
                    <v-icon class="mr-2" icon="mdi-check"></v-icon>
                    <span>Завізувати</span>
                  </v-btn>
                </template>
                <v-list>
                  <v-list-item
                    v-for="r in $directory.get('documentParticipantResults')"
                    :key="r.id"
                    :title="r.name"
                    @click="delegationAgree(r.id as number)">
                  </v-list-item>
                </v-list>
              </v-menu>
            </template>
            <v-alert
              v-else-if="delegationData.restrictionMessage"
              type="warning"
              density="compact"
              variant="tonal"
              class="text-body-2 ma-0">
              {{ delegationData.restrictionMessage }}
            </v-alert>
          </div>
        </div>

        <v-tabs class="mb-5" color="primary" slider-color="blue-darken-3">
          <v-tab :to="{ name: 'delegation-document-preview' }" replace>
            Документ
          </v-tab>
          <v-tab :to="{ name: 'delegation-document-requisites' }" replace>
            Реквізити
          </v-tab>
          <v-tab :to="{ name: 'delegation-document-route' }" replace>
            Маршрут
          </v-tab>
          <v-tab :to="{ name: 'delegation-document-attachments' }" replace>
            Вкладення
          </v-tab>
          <v-tab :to="{ name: 'delegation-document-relation' }" replace>
            Повязані документи
          </v-tab>
          <v-tab :to="{ name: 'delegation-document-comments' }" replace>
            Коментарі
          </v-tab>
        </v-tabs>

        <router-view></router-view>
      </b-section-loader>
    </v-card-text>
  </v-card>
</template>

<script setup lang="ts">
import { initDocument } from '@/pages/document/single/document'
import { BSectionLoader } from 'best-modules/components'
import { CustomDate } from 'best-modules/utils'
import { computed, reactive } from 'vue'
import { documentRouteStageParticipantSetResult } from '@/requests/document'
import { handleAsync, setSnackbar } from 'best-modules/plugins'
import { fillDirectory } from '@/directory'
import { userDelegationDocument } from '@/requests/user'
import { useRoute } from 'vue-router'
import { UserDelegationDocument } from '@/utils/types/user'
import { DocumentRouteStageParticipant } from '@/utils/types/documentRoute'
import { initComments } from '@/components/comments/v2'
import { keyToLabel } from '@/utils/key-to-label'
import { getStatusColor } from '@/pages/document/helpers'

defineOptions({
  name: 'DelegationDocumentSingle',
})

const route = useRoute()

const {
  documentId,
  documentSource,
  activeRoute,
  currentStage,
  setDocument,
  getActiveRoute,
  getAttachmentList,
  getRelationList,
  getVersionList,
} = initDocument({ disabled: true })
initComments({
  entityId: documentId.value,
  entityTypeId: 11,
})

const delegationId = computed<number>(() => Number(route.params.delegationId))

const delegationData = reactive<UserDelegationDocument>(
  {} as UserDelegationDocument
)

const getDocument = async () => {
  const result = await userDelegationDocument(
    delegationId.value,
    documentId.value
  )
  Object.assign(delegationData, result)
  setDocument(result.document)
  activeRoute.value = result.route
}

handleAsync('init', async () => {
  await getDocument()
  await getAttachmentList()
  await getRelationList()
  await getVersionList()
  await fillDirectory('documentParticipantResults')
})

const delegationPendingParticipant =
  computed<DocumentRouteStageParticipant | null>(() => {
    if (!delegationData.canApprove || !currentStage.value) {
      return null
    } else {
      return (
        currentStage.value.participants.find((p: any) => !p.resultId) || null
      )
    }
  })

const delegationAgree = (resultId: number) => {
  if (!delegationPendingParticipant.value) return
  documentRouteStageParticipantSetResult(
    [delegationPendingParticipant.value.id],
    resultId
  )
    .then(getActiveRoute)
    .catch(e => {
      const msg =
        e?.response?.data?.errors?.message?.[0] ||
        e?.response?.data?.message ||
        'Помилка виконання дії'
      setSnackbar({ text: msg, color: 'error' })
    })
}
</script>
